import { InputBase, InputBaseProps, Stack, styled, Tooltip } from "@mui/material";
import { MdsTimerOffXs } from "@sinch/icons";
import { omit, propEq } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { forwardRef, LegacyRef, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { usePriority } from "@sinch/components/application/common/usePriority";
import { useRateRounding } from "@sinch/components/application/RateRounding/useRateRounding";
import { useRoundingLabels } from "@sinch/components/application/RateRounding/useRoundingLabels";
import type { IntervalSelectValue } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/types";
import { ReadOnlyInputStatusChip } from "@sinch/components/application/RateInterval/ReadOnlyInputStatusChip";
import { MinUnitsLabel } from "@sinch/components/application/RateInterval/MinUnitsLabel";

export const ReadOnlyInput = forwardRef(
  (
    props: NonNullable<NonNullable<InputBaseProps["slotProps"]>["input"]> & { value: IntervalSelectValue },
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const { t } = useTranslation(["common", "draft"]);
    const { getColorForCustomPriority, getPriorityColor, getPriorityLabel, isNamedPriority } = usePriority();
    const { getRounding } = useRateRounding();
    const { formatRounding, formatRoundingCompact } = useRoundingLabels();
    const intervalValue = props.value;

    const transformedValue =
      intervalValue === null
        ? null
        : { type: intervalValue?.unit.type, customIntervalId: intervalValue?.unit.customIntervalId };

    // the minimum is an object in everything the builder saves, but the API allows null
    const minimumHours = intervalValue?.minimum?.amount;
    const priority = intervalValue?.unit.priority;
    const override = intervalValue?.minimum?.overrideWorkedHours;
    const rounding = getRounding(intervalValue?.roundingId);
    const selectedOption = props.options?.find(propEq("value", transformedValue));
    const hasMinimumHours = Boolean(minimumHours);
    const hasPriority = isNotNil(priority);
    const isEmptySelection = intervalValue?.unit.type === null;

    const renderMinimumHoursChip = () =>
      hasMinimumHours &&
      intervalValue?.unit.type && (
        <ReadOnlyInputStatusChip
          label={
            <Stack alignItems="center" direction="row" gap={0.5}>
              <Suspense>
                <MinUnitsLabel minimumHours={minimumHours} unitType={intervalValue.unit.type} />
              </Suspense>
              {override && (
                <Tooltip title={t("RateBuilder.overrideActualWorkedTime")}>
                  <MdsIcon fontSize="xs" icon={MdsTimerOffXs} />
                </Tooltip>
              )}
            </Stack>
          }
        />
      );

    const renderPriorityChip = () =>
      hasPriority && (
        <ReadOnlyInputStatusChip
          backgroundColor={isNamedPriority(priority) ? getPriorityColor(priority) : getColorForCustomPriority(priority)}
          label={
            isNamedPriority(priority) ? `${getPriorityLabel(priority)} (${priority})` : `${t("priority")}: ${priority}`
          }
        />
      );

    // a rule-level rounding override, the chip shows the rule itself ("≈ 1h ↑ (+ 15m)"), the tooltip names the definition
    const renderRoundingChip = () =>
      isNotNil(rounding) && (
        <Tooltip title={`${t("RateBuilder.rounding")}: ${rounding.name} (${formatRounding(rounding)})`}>
          <span>
            <ReadOnlyInputStatusChip label={formatRoundingCompact(rounding)} />
          </span>
        </Tooltip>
      );

    return (
      <ReadOnlyInputRoot>
        {isEmptySelection ? (
          <InputBase placeholder={t("RateBuilder.selectRate")} readOnly />
        ) : (
          <>
            <span>{selectedOption?.label}</span>
            {renderMinimumHoursChip()}
            {renderPriorityChip()}
            {renderRoundingChip()}
          </>
        )}
        <input
          ref={ref}
          {...omit(["ownerState", "ref", "as", "value"], props)}
          style={{ display: "none" }}
          value={intervalValue ?? ""}
        />
      </ReadOnlyInputRoot>
    );
  }
);

ReadOnlyInput.displayName = "ReadOnlyInput";

const ReadOnlyInputRoot = styled("div")({
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "100%",
  gap: 8,
});
